// ============================================================================
// Editor de tienda — estado local.
//
// El flujo es: BD -> EditorState -> Preview, y al guardar EditorState -> BD.
// Este módulo NO toca la base de datos: solo mantiene el estado del editor en
// memoria (tema + editor), notifica cambios a los suscriptores (preview y
// controles) y aplica reglas de plan sobre la marcha.
// ============================================================================

import { normalizarEditorTienda } from '../api/editor-tienda.js';
import { normalizarPerfilEmpresa } from '../api/empresa-perfil.js';
import { normalizarTemaVisual } from '../api/tema.js';

import { TEMAS_PREDETERMINADOS } from './temas.js';

export class EditorState {
  /**
   * @param {object} params
   * @param {object} params.tema - tema_visual normalizado (colores/tipografía/...)
   * @param {object} params.editor - editor_tienda normalizado (header/botones/...)
   * @param {object} params.empresa - objeto empresa (para gating por plan)
   * @param {function} params.tieneFeature - (empresa, clave) => boolean
   */
  constructor({ tema, editor, empresa, tieneFeature }) {
    this.tema = normalizarTemaVisual(tema);
    this.editor = normalizarEditorTienda(editor);
    this.empresa = empresa;
    this.tieneFeature = tieneFeature;
    const perfil = normalizarPerfilEmpresa(empresa?.empresa_perfil ?? empresa?.perfil);
    this.identidad = {
      nombre: empresa?.nombre ?? '',
      perfil: { ...perfil },
    };
    this.suscriptores = new Set();
  }

  suscribir(fn) {
    this.suscriptores.add(fn);
    return () => this.suscriptores.delete(fn);
  }

  _notificar() {
    this.suscriptores.forEach((fn) => fn(this));
  }

  // ---- Tema (colores / tipografía / sombras / espaciado) -----------------
  setTheme(cambios) {
    this.tema = normalizarTemaVisual({ ...this.tema, ...cambios });
    this._notificar();
  }

  aplicarPreset(presetId) {
    const preset = TEMAS_PREDETERMINADOS.find((p) => p.id === presetId);
    if (!preset) return false;
    this.tema = normalizarTemaVisual({ ...this.tema, ...preset.paleta });
    this.editor = normalizarEditorTienda({ ...this.editor, tema_preset: presetId });
    this._notificar();
    return true;
  }

  // ---- Editor (header / botones / tarjetas / secciones) ------------------
  setHeader(headerId) {
    this.editor = normalizarEditorTienda({ ...this.editor, header: headerId });
    this._notificar();
  }

  setCategoriasVariante(varianteId) {
    this.editor = normalizarEditorTienda({ ...this.editor, categorias_variante: varianteId });
    this._notificar();
  }

  // ---- Secciones de la portada (presentación) ----------------------------
  // Cada setter actualiza la PRESENTACIÓN de una sección dentro de
  // `editor_tienda`. El contenido (banners, productos, testimonios...) sigue
  // viviendo en sus módulos; aquí solo cambia CÓMO se ve cada sección.

  setBanner(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      banner: { ...this.editor.banner, ...cambios },
    });
    this._notificar();
  }

  setPropuestaValor(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      propuesta_valor: { ...this.editor.propuesta_valor, ...cambios },
    });
    this._notificar();
  }

  setProductos(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      productos: { ...this.editor.productos, ...cambios },
    });
    this._notificar();
  }

  setTestimonios(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      testimonios: { ...this.editor.testimonios, ...cambios },
    });
    this._notificar();
  }

  setContacto(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      contacto: { ...this.editor.contacto, ...cambios },
    });
    this._notificar();
  }

  setFooter(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      footer: { ...this.editor.footer, ...cambios },
    });
    this._notificar();
  }

  setBotones(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      botones: { ...this.editor.botones, ...cambios },
    });
    this._notificar();
  }

  setTarjetas(cambios) {
    this.editor = normalizarEditorTienda({
      ...this.editor,
      tarjetas: { ...this.editor.tarjetas, ...cambios },
    });
    this._notificar();
  }

  setSeccionOrden(orden) {
    this.editor = normalizarEditorTienda({ ...this.editor, seccion_orden: orden });
    this._notificar();
  }

  // ---- Identidad (nombre / logo / logo blanco / favicon) -----------------
  setNombreTienda(nombre) {
    this.identidad = { ...this.identidad, nombre };
    this._notificar();
  }

  /**
   * @param {'logo_url'|'logo_blanco_url'|'favicon_url'} campo - columna de empresa_perfil
   * @param {string} url - URL pública del archivo ya subido a Storage
   */
  setImagenPerfil(campo, url) {
    this.identidad = {
      ...this.identidad,
      perfil: { ...this.identidad.perfil, [campo]: url },
    };
    this._notificar();
  }

  // Devuelve el snapshot para persistir (EditorState -> BD).
  snapshot() {
    return {
      tema: this.tema,
      editor: this.editor,
      identidad: {
        nombre: this.identidad.nombre,
        perfil: { ...this.identidad.perfil },
      },
    };
  }

  /**
   * Rehidrata el estado desde datos persistidos en BD. Se usa justo después
   * de "Guardar cambios" para que el editor (y su preview) reflejen
   * EXACTAMENTE lo que quedó guardado — nombre, logos, tema y editor — en
   * lugar de una copia en memoria que podría haberse desincronizado.
   *
   * @param {object} datos
   * @param {object} [datos.tema] - tema_visual recién persistido
   * @param {object} [datos.editor] - editor_tienda recién persistido
   * @param {object} [datos.empresa] - empresa recién persistida (rehidrata identidad)
   */
  reconciliar({ tema, editor, empresa } = {}) {
    if (tema) this.tema = normalizarTemaVisual(tema);
    if (editor) this.editor = normalizarEditorTienda(editor);
    if (empresa) {
      this.empresa = empresa;
      const perfil = normalizarPerfilEmpresa(empresa.empresa_perfil ?? empresa.perfil);
      this.identidad = {
        nombre: empresa.nombre ?? '',
        perfil: { ...perfil },
      };
    }
    this._notificar();
  }
}
